import { useMemo } from 'react';
import { NoteType } from '@lio/rule-engine';
import type { DerivedScopeState, NotesDialogContext, NotesEntityItem } from './types';

// Legacy tab groupings retained until UI fully migrates; replaced by a unified tab key downstream.
export type CustomerTabs = 'customer' | 'policy';
export type PolicyTabs = 'policy' | 'transactions' | 'all';

interface UseNotesScopeArgs {
  context: NotesDialogContext;
  selectedTab: CustomerTabs | PolicyTabs | string; // allow future unified tabs
  customerId: string;
  quoteId?: string; // if context=quote
  basePolicyId?: string; // if context=policy
  selectedEntity?: NotesEntityItem | null; // unified selected policy/transaction/quote entity
  // Backward compatibility (will be removed):
  selectedPolicy?: { id: string } | null;
  selectedTransaction?: { id: string; transactionDate?: string; boundDate?: string } | null;
}

export const useNotesScope = ({
  context,
  selectedTab,
  customerId,
  quoteId,
  basePolicyId,
  selectedEntity,
}: UseNotesScopeArgs): DerivedScopeState => {
  return useMemo(() => {
    switch (context) {
      case 'quote':
        return {
          params: { quoteId, type: NoteType.Note, customerId },
          composerEnabled: true,
        } as DerivedScopeState;
      case 'customer':
        if (selectedTab === 'customer') {
          return {
            params: { type: NoteType.Note, customerId, policyId: null, quoteId: null },
            composerEnabled: true,
          } as DerivedScopeState;
        }
        if (selectedTab === 'quote') {
          return {
            params: { type: NoteType.Note, quoteId: selectedEntity?.id, policyId: null },
            composerEnabled: false,
          } as DerivedScopeState;
        } else {
          return {
            params: { type: NoteType.Note, policyId: selectedEntity?.id },
            composerEnabled: false,
          } as DerivedScopeState;
        }
      case 'policy':
        if (selectedEntity) {
          return {
            params: { quoteId: selectedEntity.id, customerId, type: NoteType.Note },
            composerEnabled: false,
          } as DerivedScopeState;
        }

        if (selectedTab === 'policy') {
          // Policy-level notes (no transactions)
          return {
            params: { policyId: basePolicyId, quoteId: null, type: NoteType.Note, customerId },
            composerEnabled: true,
          } as DerivedScopeState;
        }

        // all policy+transaction notes
        return {
          params: { policyId: basePolicyId, type: NoteType.Note, customerId },
          composerEnabled: true,
        } as DerivedScopeState;

      default:
        break;
    }

    // fallback (should not hit here)
    return {
      params: { policyId: basePolicyId, type: NoteType.Note, customerId, quoteId },
      composerEnabled: true,
    } as DerivedScopeState;
  }, [context, customerId, quoteId, selectedTab, basePolicyId, selectedEntity]);
};
